import React, { PropsWithChildren } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove as dndArrayMove,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type SortEndHandler = (params: { oldIndex: number; newIndex: number }) => void;

export interface SimpleSortableItemProps {
  id: string | number;
}

export const SimpleSortableItem: React.FC<PropsWithChildren<SimpleSortableItemProps>> = ({
  id,
  children
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

interface SimpleSortableListProps {
  ids: Array<string | number>;
  onSortEnd: SortEndHandler;
}

export const SimpleSortableList: React.FC<PropsWithChildren<SimpleSortableListProps>> = ({
  ids,
  onSortEnd,
  children
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onSortEnd({ oldIndex, newIndex });
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

export { dndArrayMove as arrayMove };

